import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import './ProductCostPricePage.css';
import PageHeader from '../../../shared/PageHeader';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { todayInTenant, formatDate } from '../../../shared/tenantClock';
import DateInput from '../../../shared/DateInput';
import NumberInput from '../../../shared/NumberInput';
import OverrideRevertBadge from '../../../shared/OverrideRevertBadge';
import TaxBasisLine from '../../../shared/TaxBasisLine';
import { formatMoney, formatNumber, getTenantHomeCurrency } from '../../../shared/numberFormat';
import { askConfirm } from '../../../shared/AppDialogs';

// '—' for blank; `currency` marks a Supplier's foreign cost.
function formatCost(value, currency) {
  return formatMoney(value, { currency, decimals: 2 }) || '—';
}

// A superseded (past Effective To) entry fades out and can't be edited or
// deleted, only the current open-ended entry can.
const PILL_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 12,
  fontWeight: 600,
  padding: '3px 9px',
  borderRadius: 999,
  whiteSpace: 'nowrap',
  color: '#b91c1c',
  background: '#fef2f2',
};

const RATE_MISSING_STYLE = { ...PILL_STYLE, color: '#92400e', background: '#fef3c7' };

function RateMissingPill() {
  return (
    <span style={RATE_MISSING_STYLE} title="No reference rate could be retrieved for this date. Enter the rate, or it's filled in once one can be retrieved.">
      Rate missing
    </span>
  );
}

function LockedPill({ effectiveTo }) {
  return (
    <span style={PILL_STYLE} title={`This cost period ended ${formatDate(effectiveTo)} and is now closed history — its cost and dates can't be changed, and it can't be deleted`}>
      Locked
    </span>
  );
}

// The only date a new entry's Effective From is ever allowed to be, once the
// Product already has at least one entry — the day after the last entry's
// Effective To. Pre-computed as a default (still editable) — the backend is
// the actual source of truth on overlap/contiguity either way.
function nextEffectiveFrom(effectiveTo) {
  const d = new Date(`${effectiveTo}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

function dayBefore(dateStr) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

function rateText(rate) {
  return rate === null || rate === undefined ? '' : String(Number(rate));
}

// A cost is held ex GST to 4 decimal places (issue #325) — one typed inc
// GST is converted at the default GST rate before it's saved.
function incToEx(inc, taxRate) {
  return Math.round((inc / (1 + taxRate)) * 10000) / 10000;
}

// One schedule entry's row — editable in place (cost + the open-ended
// entry's Effective To) while current, read-only once locked. A foreign-
// currency entry's exchange rate (issue #303) stays editable even once
// locked: the ↺ badge marks a rate staff entered and reverts it to the
// reference rate for the entry's Effective From.
function ScheduleRow({ entry, isLast, currencyCode, showRate, showGst, showBaseUnit, baseUomCode, taxRate, onEditEntry, onDeleteEntry, canDelete }) {
  const [costDraft, setCostDraft] = useState(String(entry.cost));
  const [closingDate, setClosingDate] = useState(entry.effective_to || '');
  const [rateDraft, setRateDraft] = useState(rateText(entry.exchange_rate));
  const showError = useErrorToast();

  useEffect(() => {
    setCostDraft(String(entry.cost));
    setClosingDate(entry.effective_to || '');
    setRateDraft(rateText(entry.exchange_rate));
    // eslint-disable-next-line
  }, [entry.id, entry.cost, entry.effective_to, entry.exchange_rate]);

  const handleRateBlur = async () => {
    const val = Number(rateDraft);
    if (rateDraft === '' || Number.isNaN(val) || val <= 0 || (entry.exchange_rate !== null && val === Number(entry.exchange_rate))) {
      setRateDraft(rateText(entry.exchange_rate));
      return;
    }
    try {
      await onEditEntry(entry.id, { exchange_rate: val });
    } catch (err) {
      showError(err, 'Could not update the exchange rate');
      setRateDraft(rateText(entry.exchange_rate));
    }
  };

  const handleRevertRate = async () => {
    try {
      await onEditEntry(entry.id, { use_reference_rate: true });
    } catch (err) {
      showError(err, 'Could not restore the reference rate');
    }
  };

  const handleCostBlur = async () => {
    const val = Number(costDraft);
    if (costDraft === '' || Number.isNaN(val) || val < 0 || val === Number(entry.cost)) {
      setCostDraft(String(entry.cost));
      return;
    }
    try {
      await onEditEntry(entry.id, { [entry.costFieldName]: val });
    } catch (err) {
      showError(err, 'Could not update Cost Price');
      setCostDraft(String(entry.cost));
    }
  };

  const handleEffectiveToBlur = async () => {
    if (closingDate === (entry.effective_to || '')) return;
    try {
      await onEditEntry(entry.id, { effective_to: closingDate || null });
    } catch (err) {
      showError(err, 'Could not update Effective To');
      setClosingDate(entry.effective_to || '');
    }
  };

  const rate = entry.exchange_rate === null || entry.exchange_rate === undefined ? null : Number(entry.exchange_rate);
  const homeValue = showRate && rate !== null ? Number(entry.cost) * rate : null;
  // The Inc GST figure follows the ex GST cost as it's typed.
  const shownExCost = entry.locked || costDraft === '' || Number.isNaN(Number(costDraft)) ? Number(entry.cost) : Number(costDraft);

  return (
    <tr className="list-row">
      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', opacity: entry.locked ? 0.6 : 1 }}>{formatDate(entry.effective_from)}</td>
      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', opacity: entry.locked ? 0.6 : 1 }}>
        {!entry.locked && isLast ? (
          <DateInput
            className="styled-input"
            value={closingDate}
            onChange={(e) => setClosingDate(e.target.value)}
            onBlur={handleEffectiveToBlur}
            min={entry.effective_from}
            style={{ width: 140 }}
          />
        ) : (formatDate(entry.effective_to) || 'open-ended')}
      </td>
      <td style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap', opacity: entry.locked ? 0.6 : 1 }}>
        {entry.locked ? formatCost(entry.cost, currencyCode) : (
          <NumberInput
            value={costDraft}
            onChange={(e) => setCostDraft(e.target.value)}
            onBlur={handleCostBlur}
            min="0"
            minDecimals={2}
            maxDecimals={4}
            style={{ width: 100, textAlign: 'right' }}
          />
        )}
        {showBaseUnit && <span style={{ marginLeft: 6, color: '#5a6b85' }}>per {entry.uomCode}</span>}
      </td>
      {showBaseUnit && (
        <td style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap', color: '#888', opacity: entry.locked ? 0.6 : 1 }}>
          {formatCost(entry.baseCost, currencyCode)} per {baseUomCode}
        </td>
      )}
      {showGst && (
        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#888', opacity: entry.locked ? 0.6 : 1 }}>
          {taxRate === null ? '—' : formatCost(shownExCost * (1 + taxRate))}
        </td>
      )}
      {showRate && (
        <td style={{ padding: '8px 12px', textAlign: 'right' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {entry.exchange_rate_entered && (
              <OverrideRevertBadge
                onClick={handleRevertRate}
                title={`Entered rate — restore the reference rate for ${formatDate(entry.effective_from)}`}
              />
            )}
            {rate === null && <RateMissingPill />}
            <NumberInput
              value={rateDraft}
              onChange={(e) => setRateDraft(e.target.value)}
              onBlur={handleRateBlur}
              min="0"
              minDecimals={4}
              maxDecimals={6}
              title={entry.exchange_rate_entered ? 'Rate entered by staff' : `Reference rate for ${formatDate(entry.effective_from)}`}
              style={{ width: 100, textAlign: 'right' }}
            />
          </span>
        </td>
      )}
      {showRate && (
        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#888', opacity: entry.locked ? 0.6 : 1 }}>
          {homeValue !== null ? formatCost(homeValue) : '—'}
        </td>
      )}
      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
        {entry.locked ? <LockedPill effectiveTo={entry.effective_to} /> : (
          canDelete && (
            <button type="button" className="action-button" style={{ color: '#b91c1c', background: '#fef2f2', borderColor: '#fecaca' }} onClick={() => onDeleteEntry(entry.id)}>Delete</button>
          )
        )}
      </td>
    </tr>
  );
}

// Add-a-cost form — Effective From defaults from the schedule's current
// state (day after the last entry's Effective To), editable either way. A
// foreign-currency cost's exchange rate is pre-filled with the reference
// rate for Effective From (issue #303); typing over it sends the entered
// rate, leaving it sends none and the server gives the entry the reference
// rate.
function AddCostForm({ productId, schedule, costFieldName, currencyCode, showRate, showGst, taxRate, buyUoms, baseUomCode, onAddEntry, onScheduleNewCost }) {
  const [newEffectiveFrom, setNewEffectiveFrom] = useState('');
  const [pickedUomId, setPickedUomId] = useState('');
  const [gstMode, setGstMode] = useState('ex');
  const [newEffectiveTo, setNewEffectiveTo] = useState('');
  const [newCost, setNewCost] = useState('');
  const [referenceRate, setReferenceRate] = useState(null);
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [enteredRate, setEnteredRate] = useState(null);
  const [saving, setSaving] = useState(false);
  const showError = useErrorToast();
  const dismissError = useDismissError();

  const firstEntry = schedule.length ? schedule[0] : null;
  const lastEntry = schedule.length ? schedule[schedule.length - 1] : null;
  const scheduleOpen = lastEntry && !lastEntry.effective_to;

  const defaultEffectiveFrom = lastEntry && lastEntry.effective_to ? nextEffectiveFrom(lastEntry.effective_to) : '';
  const effectiveFromValue = newEffectiveFrom || defaultEffectiveFrom;
  // Backfilling retrospective history before the schedule's current earliest
  // entry — this never touches/closes the existing last entry.
  const isBackfill = !!(firstEntry && effectiveFromValue && effectiveFromValue < firstEntry.effective_from);

  useEffect(() => {
    if (!showRate || !effectiveFromValue) {
      setReferenceRate(null);
      return undefined;
    }
    let cancelled = false;
    setReferenceLoading(true);
    axios.get(`/api/products/${productId}/cost-prices/reference-rate`, { params: { date: effectiveFromValue } })
      .then((res) => { if (!cancelled) setReferenceRate(res.data.data?.rate ?? null); })
      .catch(() => { if (!cancelled) setReferenceRate(null); })
      .finally(() => { if (!cancelled) setReferenceLoading(false); });
    return () => { cancelled = true; };
  }, [productId, showRate, effectiveFromValue]);

  const rateValue = enteredRate !== null ? enteredRate : rateText(referenceRate);

  // Inc-GST entry needs a default GST rate to convert at.
  const entryGstMode = showGst && taxRate !== null ? gstMode : 'ex';
  const typedCost = newCost === '' || Number.isNaN(Number(newCost)) ? null : Number(newCost);
  // The UoM the cost is entered per (issue #364): the only Can Buy UoM, the
  // one picked when there are several, or the Base UoM when there's none.
  const entryUom = buyUoms.length === 1 ? buyUoms[0] : (buyUoms.find((u) => String(u.uom_id) === String(pickedUomId)) || null);
  const typedExCost = typedCost === null ? null : (entryGstMode === 'inc' ? incToEx(typedCost, taxRate) : typedCost);
  const baseUnitFigure = typedExCost === null || !entryUom || Number(entryUom.conversion_rate) === 1 ? null : (
    `${formatMoney(Math.round((typedExCost / entryUom.conversion_rate) * 10000) / 10000, { currency: currencyCode, decimals: 4 })} per ${baseUomCode}${showGst ? ' ex GST' : ''}`
  );
  const otherGstFigure = typedCost === null || !showGst || taxRate === null ? null : (
    entryGstMode === 'inc' ? `${formatMoney(incToEx(typedCost, taxRate), { decimals: 4 })} ex GST` : `${formatMoney(typedCost * (1 + taxRate), { decimals: 4 })} inc GST`
  );

  const resetFields = () => {
    setNewEffectiveFrom('');
    setNewEffectiveTo('');
    setNewCost('');
    setEnteredRate(null);
    setPickedUomId('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!effectiveFromValue) return showError(null, 'Effective From is required.');
    const cost = Number(newCost);
    if (newCost === '' || Number.isNaN(cost) || cost < 0) return showError(null, 'Cost must be zero or a positive number.');
    if (buyUoms.length > 1 && !entryUom) return showError(null, 'Pick which Unit of Measure this cost is for.');
    const rate = enteredRate !== null && enteredRate !== '' ? Number(enteredRate) : null;
    if (rate !== null && (Number.isNaN(rate) || rate <= 0)) return showError(null, 'Exchange rate must be greater than 0.');
    setSaving(true);
    try {
      const exCost = entryGstMode === 'inc' ? incToEx(cost, taxRate) : cost;
      const payload = { effective_from: effectiveFromValue, effective_to: newEffectiveTo || null, [costFieldName]: exCost };
      if (entryUom) payload.uom_id = entryUom.uom_id;
      if (showRate && rate !== null && rate !== referenceRate) payload.exchange_rate = rate;
      if (isBackfill) {
        await onAddEntry(payload);
      } else if (scheduleOpen) {
        await onScheduleNewCost(lastEntry.id, dayBefore(effectiveFromValue), payload);
      } else {
        await onAddEntry(payload);
      }
      resetFields();
      dismissError();
    } catch (err) {
      showError(err, 'Could not save Cost Price entry');
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 12 }}>
      <div>
        <label style={{ fontSize: 12, color: '#5a6b85' }}>Effective From</label><br />
        <DateInput
          className="styled-input"
          value={effectiveFromValue}
          onChange={(e) => setNewEffectiveFrom(e.target.value)}
          style={{ width: 140 }}
        />
      </div>
      <div>
        <label style={{ fontSize: 12, color: '#5a6b85' }}>Effective To{isBackfill ? '' : ' (optional)'}</label><br />
        <DateInput
          className="styled-input"
          value={newEffectiveTo}
          onChange={(e) => setNewEffectiveTo(e.target.value)}
          min={effectiveFromValue || undefined}
          style={{ width: 140 }}
        />
      </div>
      {showGst && (
        <div>
          <label style={{ fontSize: 12, color: '#5a6b85' }}>Enter as</label><br />
          <div
            style={{ display: 'flex', gap: 10, height: 30, alignItems: 'center' }}
            title={taxRate === null ? 'No default Tax Code configured — Inc-GST entry unavailable' : undefined}
          >
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 3 }}>
              <input type="radio" checked={entryGstMode === 'ex'} onChange={() => setGstMode('ex')} />
              Ex-GST
            </label>
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 3, opacity: taxRate === null ? 0.5 : 1 }}>
              <input type="radio" checked={entryGstMode === 'inc'} disabled={taxRate === null} onChange={() => setGstMode('inc')} />
              Inc-GST
            </label>
          </div>
        </div>
      )}
      {buyUoms.length > 1 && (
        <div>
          <label style={{ fontSize: 12, color: '#5a6b85' }}>Per</label><br />
          <select className="styled-input" value={pickedUomId} onChange={(e) => setPickedUomId(e.target.value)} style={{ width: 110 }}>
            <option value="">Pick...</option>
            {buyUoms.map((u) => <option key={u.uom_id} value={u.uom_id}>{u.uom?.code}</option>)}
          </select>
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <label style={{ fontSize: 12, color: '#5a6b85' }}>Cost ({currencyCode || getTenantHomeCurrency()}){buyUoms.length === 1 ? ` per ${buyUoms[0].uom?.code}` : ''}</label><br />
        <NumberInput
          placeholder={formatNumber(0, { decimals: 2 })}
          value={newCost}
          onChange={(e) => setNewCost(e.target.value)}
          min="0"
          minDecimals={2}
          maxDecimals={4}
          style={{ width: 110, textAlign: 'right' }}
        />
        {(otherGstFigure || baseUnitFigure) && (
          <div style={{ position: 'absolute', top: '100%', left: 0, whiteSpace: 'nowrap', marginTop: 2, fontSize: 11, color: '#888' }}>
            {otherGstFigure && <div>= {otherGstFigure}</div>}
            {baseUnitFigure && <div>= {baseUnitFigure}</div>}
          </div>
        )}
      </div>
      {showRate && (
        <div>
          <label style={{ fontSize: 12, color: '#5a6b85' }}>Rate ({getTenantHomeCurrency()} per {currencyCode})</label><br />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <NumberInput
              placeholder={referenceLoading ? 'Loading...' : 'Rate missing'}
              value={rateValue}
              onChange={(e) => setEnteredRate(e.target.value)}
              min="0"
              minDecimals={4}
              maxDecimals={6}
              title={enteredRate !== null ? 'Rate entered by staff' : `Reference rate for ${formatDate(effectiveFromValue)}`}
              style={{ width: 110, textAlign: 'right' }}
            />
            {enteredRate !== null && (
              <OverrideRevertBadge onClick={() => setEnteredRate(null)} title="Entered rate — restore the reference rate" />
            )}
          </span>
        </div>
      )}
      <button type="submit" className="primary-button" disabled={saving}>
        {saving ? 'Saving...' : (isBackfill ? '+ Add historical cost' : (scheduleOpen ? 'Schedule new cost' : '+ Add cost'))}
      </button>
    </form>
  );
}

// Cost Price Schedule page (issue #139) — the dated Cost Price schedule for
// one Product: adding/closing/deleting entries, locked once past. Reached
// either from the Product list's own kebab menu (non-Supplier-linked
// Products) or via a Supplier's Cost Price Schedule page (Supplier-linked
// Products) — same page either way, since the schedule mechanics are
// identical; only the currency and AUD-conversion column differ. A simpler
// sibling of ProductPricesPage.js: no auto-derivation. A home-currency cost
// can be typed ex or inc GST and shows both figures (issue #325); a foreign-
// currency cost is ex GST only — imported goods carry no supplier GST.
function ProductCostPricePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromSupplier = location.state?.fromSupplier;
  const showError = useErrorToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [defaultTaxRate, setDefaultTaxRate] = useState(null);

  const fetchData = async () => {
    try {
      const res = await axios.get(`/api/products/${id}`);
      setData(res.data.data);
      setLoadError('');
    } catch (err) {
      setLoadError(err.response?.data?.message || 'Could not load Cost Price Schedule');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    axios.get('/api/tax-codes').then((res) => {
      const defaultCode = (res.data.data || []).find((t) => t.is_default);
      setDefaultTaxRate(defaultCode ? Number(defaultCode.rate) / 100 : null);
    }).catch(() => setDefaultTaxRate(null));
  }, []);

  if (loading) return <div style={{ padding: 24 }}>Loading...</div>;
  if (loadError) return <div style={{ padding: 24, color: 'red' }}>{loadError}</div>;
  if (!data) return null;

  const hasSupplier = !!data.supplier;
  const costFieldName = hasSupplier ? 'cost_price_foreign' : 'cost_price';
  const currencyCode = hasSupplier ? data.supplier.currency_code : null;
  // Only a Supplier trading in a foreign currency carries a rate per entry.
  const showRate = !!currencyCode && currencyCode !== getTenantHomeCurrency();
  const showGst = !showRate;

  // Cost is entered per Buy UoM and held per Base Unit (issue #364).
  const baseUomCode = data.base_uom?.code || '—';
  const buyUoms = (data.uoms || [])
    .filter((u) => u.can_buy)
    .sort((a, b) => a.conversion_rate - b.conversion_rate);
  const showBaseUnit = (data.cost_schedule || []).some((entry) => entry.uom_id)
    || buyUoms.some((u) => String(u.uom_id) !== String(data.base_uom_id));

  const today = todayInTenant();
  const schedule = [...(data.cost_schedule || [])]
    .sort((a, b) => a.effective_from.localeCompare(b.effective_from))
    .map((entry) => ({
      ...entry,
      // The price as entered, per the entry's UoM, and the Base Unit cost.
      cost: entry.entered_price !== null && entry.entered_price !== undefined ? entry.entered_price : entry[costFieldName],
      baseCost: entry[costFieldName],
      uomCode: entry.uom?.code || baseUomCode,
      costFieldName,
      locked: !!entry.effective_to && entry.effective_to < today,
    }));
  const lastEntry = schedule.length ? schedule[schedule.length - 1] : null;

  const handleAddEntry = async (payload) => {
    await axios.post(`/api/products/${id}/cost-prices`, payload);
    await fetchData();
  };
  const handleScheduleNewCost = async (previousEntryId, previousEffectiveTo, payload) => {
    await axios.put(`/api/products/${id}/cost-prices/${previousEntryId}`, { effective_to: previousEffectiveTo });
    await axios.post(`/api/products/${id}/cost-prices`, payload);
    await fetchData();
  };
  const handleEditEntry = async (costPriceId, patch) => {
    await axios.put(`/api/products/${id}/cost-prices/${costPriceId}`, patch);
    await fetchData();
  };
  const handleDeleteEntry = async (costPriceId) => {
    if (!(await askConfirm('Delete this Cost Price entry?', { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/products/${id}/cost-prices/${costPriceId}`);
      await fetchData();
    } catch (err) {
      showError(err, 'Could not delete Cost Price entry');
    }
  };

  return (
    <div className="product-cost-price-page" style={{ width: '100%', maxWidth: 760, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '20px 24px 32px' }}>
      <PageHeader
        crumbs={fromSupplier ? [
          { label: 'Suppliers', onClick: () => navigate('/suppliers') },
          { label: fromSupplier.name, onClick: () => navigate(`/suppliers/${fromSupplier.id}/cost-prices`) },
          { label: 'Cost Price Schedule' },
        ] : [
          { label: 'Products', onClick: () => navigate('/products') },
          { label: 'Cost Price Schedule' },
        ]}
        title="Cost Price Schedule"
        subtitle={<strong>{data.sku} — {data.name}{hasSupplier ? ` · ${data.supplier.name} (${currencyCode})` : ` · No Supplier (${getTenantHomeCurrency()})`}</strong>}
      />

      {schedule.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden', marginBottom: 16 }}>
          <h4 style={{ margin: 0, padding: '10px 12px 0', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Base UoM: {baseUomCode}
          </h4>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead className="list-head">
                <tr>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Effective From</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, width: 160 }}>Effective To</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Cost ({currencyCode || getTenantHomeCurrency()}){showGst && <TaxBasisLine>ex GST</TaxBasisLine>}</th>
                  {showBaseUnit && <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Per Base Unit{showGst && <TaxBasisLine>ex GST</TaxBasisLine>}</th>}
                  {showGst && <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Cost ({currencyCode || getTenantHomeCurrency()})<TaxBasisLine>inc GST</TaxBasisLine></th>}
                  {showRate && <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Rate ({getTenantHomeCurrency()} per {currencyCode})</th>}
                  {showRate && <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{getTenantHomeCurrency()}</th>}
                  <th style={{ padding: '10px 12px' }}></th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((entry) => (
                  <ScheduleRow
                    key={entry.id}
                    entry={entry}
                    isLast={lastEntry && entry.id === lastEntry.id}
                    currencyCode={currencyCode}
                    showRate={showRate}
                    showGst={showGst}
                    showBaseUnit={showBaseUnit}
                    baseUomCode={baseUomCode}
                    taxRate={defaultTaxRate}
                    canDelete={lastEntry && entry.id === lastEntry.id}
                    onEditEntry={handleEditEntry}
                    onDeleteEntry={handleDeleteEntry}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <h4 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Add a Cost{buyUoms.length ? '' : ` — Base UoM: ${baseUomCode}`}
        </h4>
        <AddCostForm productId={id} schedule={schedule} costFieldName={costFieldName} currencyCode={currencyCode} showRate={showRate} showGst={showGst} taxRate={defaultTaxRate} buyUoms={buyUoms} baseUomCode={baseUomCode} onAddEntry={handleAddEntry} onScheduleNewCost={handleScheduleNewCost} />
      </div>
    </div>
  );
}

export default ProductCostPricePage;
